import React, { createContext, useContext } from "react";
import type { ParamOverride, ParameterSchema } from "@tradingdroid/frontends-types";

interface ParamOverridesContextValue {
  disabled: boolean;
  parameterSchema: Record<string, ParameterSchema>;
  overrides: Record<string, ParamOverride>;
  currentValues: Record<string, unknown>;
  updateOverride: (paramName: string, override: ParamOverride | undefined) => void;
}

const ParamOverridesContext = createContext<ParamOverridesContextValue | null>(null);

export const useParamOverrides = (): ParamOverridesContextValue => {
  const context = useContext(ParamOverridesContext);
  if (!context) {
    throw new Error("useParamOverrides must be used within ParamOverridesProvider");
  }
  return context;
};

interface ProviderProps {
  disabled: boolean;
  parameterSchema: Record<string, ParameterSchema>;
  overrides: Record<string, ParamOverride>;
  currentValues: Record<string, unknown>;
  updateOverride: (paramName: string, override: ParamOverride | undefined) => void;
  children: React.ReactNode;
}

export const ParamOverridesProvider: React.FC<ProviderProps> = ({
  disabled,
  parameterSchema,
  overrides,
  currentValues,
  updateOverride,
  children,
}) => {
  const value = React.useMemo(
    () => ({ disabled, parameterSchema, overrides, currentValues, updateOverride }),
    [disabled, parameterSchema, overrides, currentValues, updateOverride],
  );

  return <ParamOverridesContext.Provider value={value}>{children}</ParamOverridesContext.Provider>;
};
